import { FileDescriptor } from '@dreryk/command-query-ws-api';
import {
   MedicalInformation,
   MedicalInformationAttachment,
   MedicalInformationAttachmentCreateCommand,
   MedicalInformationAttachmentQuery,
   MedicalInformationAttachmentRemoveCommand,
   MedicalInformationAttachmentStorageLimitExceededFault,
} from '@dreryk/egabinet-patient-medical-information-frontend-api';

import {
   CqrsQueryPerformResult,
   performCqrsQuerySearch,
   useCqrsCommand,
   UseCqrsCommandResult,
} from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';
import { paginations } from '@dreryk/command-query-ws-client';

import { CertificateFault, useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';

import { ExternalDocumentationStorageLimitExceededTranslation } from '../../../common/ExternalDocumentationStorageLimitExceededTranslation';

type UseMedicalInformationAttachmentCreateCommandOptions = {
   medicalInformationId: MedicalInformation['id'];
   onAuthenticationRequired: () => void;
};

export const useMedicalInformationAttachmentCreateCommand = (
   options: UseMedicalInformationAttachmentCreateCommandOptions
): UseCqrsCommandResult<
   MedicalInformationAttachment,
   FileDescriptor,
   CertificateFault | MedicalInformationAttachmentStorageLimitExceededFault
> => {
   return useCqrsCommand(
      (parameters) => {
         const command = new MedicalInformationAttachmentCreateCommand();
         command.medicalInformationId = options.medicalInformationId;
         command.fileDescriptor = {
            file: parameters.file,
            name: parameters.name,
            contentLength: parameters.contentLength,
         };
         return command;
      },
      {
         onFault: {
            ...useCertificateFaultHandlerMap({
               onAuthenticationRequired: options.onAuthenticationRequired,
            }),
            'egabinet-patient-medical-information-frontend-api:MedicalInformationAttachmentStorageLimitExceededFault': () =>
               dispatchSnackbarNegativeEvent(<ExternalDocumentationStorageLimitExceededTranslation />),
         },
      }
   );
};

export const useMedicalInformationAttachmentRemoveCommand = (): UseCqrsCommandResult<void, number> => {
   return useCqrsCommand((id) => {
      const command = new MedicalInformationAttachmentRemoveCommand();
      command.id = id;
      return command;
   });
};

type UseMedicalInformationAttachmentPerformCqrsQuerySearchOptions = {
   medicalInformationId: MedicalInformation['id'];
};

export const useMedicalInformationAttachmentPerformCqrsQuerySearch = (
   options: UseMedicalInformationAttachmentPerformCqrsQuerySearchOptions
) => async (): CqrsQueryPerformResult<MedicalInformationAttachment[]> => {
   return performCqrsQuerySearch<MedicalInformationAttachment>(() => {
      const query = new MedicalInformationAttachmentQuery();
      query.medicalInformationId = Slot.of(options.medicalInformationId);
      return query;
   }, paginations.all());
};
